import { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import { ChevronDown, ChevronUp, Linkedin } from "lucide-react";
import { initials, photoOf, bandColor, tenureLabel, fmtDate, GREY } from "@/lib/orgUtils";

const FIELD_RENDER = {
  designation: (emp) => emp.designation,
  department: (emp, opts) => emp.department,
  date_of_joining: (emp, opts) => {
    if (!emp.date_of_joining) return null;
    const mode = opts?.doj_render || "tenure";
    if (mode === "date") return fmtDate(emp.date_of_joining);
    if (mode === "both") return `${fmtDate(emp.date_of_joining)} · ${tenureLabel(emp.date_of_joining)}`;
    return tenureLabel(emp.date_of_joining);
  },
  email: (emp) => emp.email,
  location: (emp) => emp.location,
  employment_type: (emp) => emp.employment_type,
  linkedin_url: (emp) => (emp.linkedin_url ? "LinkedIn" : null),
  highest_qualification: (emp) => emp.highest_qualification,
};

export const EmployeeCard = memo(function EmployeeCard({ emp, config, childCount, hiddenCount, collapsed, onToggle, onClick, highlighted, compact, dropTarget }) {
  const fields = config?.display_config?.fields || ["designation"];
  const opts = config?.display_config?.options || {};
  const style = config?.card_style || {};
  const color = bandColor(emp.date_of_joining, config?.color_config);
  const placement = config?.color_config?.placement || "edge";
  const photo = style.show_photo !== false ? photoOf(emp) : null;
  const missing = config?.color_config?.enabled && !emp.date_of_joining;

  const bg = placement === "full" && color ? color : "#fff";
  const textLight = placement === "full" && color;

  return (
    <div
      data-testid={`emp-card-${emp.employee_id}`}
      onClick={onClick}
      title={missing ? "DOJ missing — no tenure color" : undefined}
      className={`relative rounded-xl border-2 select-none cursor-pointer shadow-[0_2px_10px_rgba(24,27,52,0.08)] hover:shadow-[0_8px_24px_rgba(24,27,52,0.14)] hover:-translate-y-0.5 transition-transform ${
        highlighted ? "node-highlight" : ""
      } ${dropTarget ? "border-[#6161FF] ring-4 ring-[#6161FF]/25" : "border-[#181B34]/10"}`}
      style={{ width: compact ? 200 : 232, background: bg, color: textLight ? "#fff" : "#181B34" }}
    >
      {placement === "header" && color && <div className="h-2 rounded-t-[10px]" style={{ background: color }} />}
      <div className="flex items-center gap-2.5 p-3">
        {placement === "edge" && (
          <div className="absolute left-0 top-2 bottom-2 w-[5px] rounded-r" style={{ background: color || (missing ? GREY : "transparent") }} />
        )}
        {style.show_photo !== false && (
          photo ? (
            <img src={photo} alt={emp.full_name} className="w-11 h-11 rounded-full object-cover shrink-0 border-2 border-white shadow" draggable={false} />
          ) : (
            <span className="w-11 h-11 rounded-full shrink-0 flex items-center justify-center text-sm font-bold"
              style={{ background: textLight ? "rgba(255,255,255,0.25)" : "#F0F3FF", color: textLight ? "#fff" : "#6161FF" }}>
              {initials(emp.full_name)}
            </span>
          )
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="font-bold text-[13px] leading-tight truncate">{emp.full_name}</p>
            {style.show_report_count !== false && childCount > 0 && (
              <span className="shrink-0 text-[10px] font-bold px-1.5 rounded-full"
                style={{ background: textLight ? "rgba(255,255,255,0.25)" : "#6161FF14", color: textLight ? "#fff" : "#6161FF" }}>
                {childCount}
              </span>
            )}
          </div>
          {fields.map((f) => {
            const val = FIELD_RENDER[f]?.(emp, opts);
            if (!val) return null;
            if (f === "department" && opts.department_render === "tag") {
              return <span key={f} className="inline-block mt-0.5 text-[10px] font-bold px-1.5 py-px rounded"
                style={{ background: textLight ? "rgba(255,255,255,0.22)" : "#579BFC1f", color: textLight ? "#fff" : "#2b6cb8" }}>{val}</span>;
            }
            if (f === "linkedin_url") {
              return <span key={f} className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold opacity-80"><Linkedin size={10} /> {val}</span>;
            }
            return <p key={f} className={`text-[11px] leading-tight truncate ${textLight ? "text-white/85" : "text-[#676a82]"}`}>{val}</p>;
          })}
        </div>
      </div>
      {childCount > 0 && onToggle && (
        <button
          data-testid={`collapse-toggle-${emp.employee_id}`}
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 h-6 min-w-6 px-1 rounded-full bg-white border border-[#181B34]/15 shadow flex items-center justify-center gap-0.5 text-[10px] font-bold text-[#4b4e63] hover:border-[#6161FF] hover:text-[#6161FF] z-10"
        >
          {collapsed ? <><ChevronDown size={12} />+{hiddenCount}</> : <ChevronUp size={12} />}
        </button>
      )}
    </div>
  );
});

export default function EmployeeNode({ data }) {
  const { emp, childCount, hiddenCount, collapsed, config, onToggle, onSelect, highlighted, dropTarget, horizontal } = data;
  return (
    <>
      <Handle type="target" position={horizontal ? Position.Left : Position.Top} style={{ opacity: 0 }} />
      <EmployeeCard
        emp={emp} config={config} childCount={childCount} hiddenCount={hiddenCount}
        collapsed={collapsed} highlighted={highlighted} dropTarget={dropTarget}
        onToggle={() => onToggle(emp.id)} onClick={() => onSelect(emp)}
      />
      <Handle type="source" position={horizontal ? Position.Right : Position.Bottom} style={{ opacity: 0 }} />
    </>
  );
}
